import { useLLMSearchAssistant } from '@ltvco/refresh-lib/hooks';
import { useState, useRef, useEffect } from 'react';

import { SearchpalMessage, SearchpalMessageBlock } from '../types';
import {
  allFilterLabels,
  MESSAGES_SECTION_TYPES,
  ROLES,
} from '../utils/constants';
import { getCurrentTime } from '../utils/conversationUtils';
import { filterAllowedKeys } from '../utils/filterUtils';

import { useFilterManager } from './useFilterManager';

export const useSearchPalConversation = () => {
  const [messages, setMessages] = useState<SearchpalMessage[]>([]);
  const [isGeneratingResponse, setIsGeneratingResponse] = useState(false);
  const [initialMessageSent, setInitialMessageSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);

  const {
    pendingFunctionCall,
    applyPendingFilters,
    processResponseForFunctionCalls,
    pendingFilters,
    setPendingFilters,
    getCurrentFilters,
    handleResetFilters,
  } = useFilterManager();

  const searchAssistantMutation = useLLMSearchAssistant({
    shouldSuspendExecution: false,
  });
  const addMessage = (message: SearchpalMessage) => {
    setMessages((prev) => [...prev, message]);
  };

  const handleUserMessage = async (text: string) => {
    if (pendingFunctionCall) {
      await applyPendingFilters(true);
    }
    setIsGeneratingResponse(true);
    try {
      const response = await searchAssistantMutation.mutateAsync({
        userInput: text,
      });
      if (response) {
        const res = await processResponseForFunctionCalls(response);
        if (res.isFunction && res.filters) {
          const mergedFilters = {
            ...filterAllowedKeys(pendingFilters ?? {}, false, true),
            ...filterAllowedKeys(res.filters, false, true),
          };
          setPendingFilters(mergedFilters);
          const currentFiltersNormalized = filterAllowedKeys(
            getCurrentFilters(),
            false
          );

          const filtersObj: Record<string, any> = {
            ...currentFiltersNormalized,
            ...mergedFilters,
          };

          const missingFilters = allFilterLabels.filter(
            ({ key }) => !filtersObj[key]
          );
          const blocks: SearchpalMessageBlock[] = [
            { type: MESSAGES_SECTION_TYPES.plain, text: 'Your new filters:' },
            { type: MESSAGES_SECTION_TYPES.filters, filters: mergedFilters },
            {
              type: MESSAGES_SECTION_TYPES.plain,
              text: 'Would you like to apply these filters now?',
            },
          ];
          if (missingFilters.length > 0) {
            blocks.push({
              type: MESSAGES_SECTION_TYPES.suggestions,
              suggestions: missingFilters.map((f) => f.label).slice(0, 5),
            });
          }
          addMessage({
            blocks,
            role: ROLES.assistant,
            timestamp: getCurrentTime(),
            shouldShowConfirmChip: true,
          });
        } else {
          addMessage({
            blocks: [
              {
                type: MESSAGES_SECTION_TYPES.plain,
                text: "I couldn't detect a valid filter. Can you give me some context about that filter?",
              },
            ],
            role: ROLES.assistant,
            timestamp: getCurrentTime(),
            shouldShowConfirmChip: false,
          });
        }
      }
      return response;
    } catch (error) {
      console.error('Error sending message:', error);
      addMessage({
        blocks: [
          {
            type: MESSAGES_SECTION_TYPES.plain,
            text: 'Sorry, I encountered an error. Please try again.',
          },
        ],
        role: ROLES.assistant,
        timestamp: getCurrentTime(),
        shouldShowConfirmChip: false,
      });
      return null;
    } finally {
      setIsGeneratingResponse(false);
    }
  };

  const handleInitialMessage = () => {
    if (initialMessageSent) {
      return;
    }
    const baseFilters = filterAllowedKeys(getCurrentFilters(), true);

    setInitialMessageSent(true);
    if (Object.keys(baseFilters).length === 0) {
      addMessage({
        blocks: [
          {
            type: MESSAGES_SECTION_TYPES.plain,
            text: "Welcome! I'm your search assistant. How can I help you find someone today?",
          },
        ],
        role: ROLES.assistant,
        timestamp: getCurrentTime(),
        shouldShowConfirmChip: false,
      });
      return;
    }
    const missingFilters = allFilterLabels.filter(
      ({ key }) => !baseFilters[key as keyof typeof baseFilters]
    );
    const blocks: SearchpalMessageBlock[] = [
      {
        type: MESSAGES_SECTION_TYPES.plain,
        text: 'You have set the following filters:',
      },
      { type: MESSAGES_SECTION_TYPES.filters, filters: baseFilters },
    ];
    if (missingFilters.length > 0) {
      blocks.push({
        type: MESSAGES_SECTION_TYPES.suggestions,
        suggestions: missingFilters.map((f) => f.label).slice(0, 5),
      });
    }
    addMessage({
      blocks,
      role: ROLES.assistant,
      timestamp: getCurrentTime(),
      shouldShowConfirmChip: false,
    });
  };

  const handleApplyPendingFilters = async (cancelled?: boolean) => {
    setIsGeneratingResponse(true);
    await applyPendingFilters(cancelled);
    addMessage({
      blocks: [
        {
          type: MESSAGES_SECTION_TYPES.plain,
          text: 'We found results based on your filters. You can review them now or add more filters to narrow it down further.',
        },
      ],
      role: ROLES.assistant,
      timestamp: getCurrentTime(),
      shouldShowConfirmChip: false,
      shouldShowViewResultsChip: true,
    });
    setIsGeneratingResponse(false);
  };

  const handleReset = async () => {
    setMessages([]);
    setPendingFilters({});

    await handleResetFilters();
    setInitialMessageSent(false);
  };

  useEffect(() => {
    if (!initialMessageSent) {
      handleInitialMessage();
    }
  }, [initialMessageSent]);

  return {
    pendingFilters,
    messages,
    isGeneratingResponse,
    initialMessageSent,
    messagesEndRef,
    addMessage,
    handleUserMessage,
    handleInitialMessage,
    applyPendingFilters: handleApplyPendingFilters,
    handleReset,
  };
};
